import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ResponsiveChartBox } from '../common/ResponsiveChartBox';
import { TrendingUp, Assessment, GetApp } from '@mui/icons-material';
import { ExpenseCategory } from '@hostel-manager/shared';

interface ExpenseReport {
  reportPeriod: {
    startDate: Date;
    endDate: Date;
    daysIncluded: number;
  };
  summary: {
    totalExpenses: number;
    totalTransactions: number;
    averageExpenseAmount: number;
    topCategory: ExpenseCategory;
    topCategoryAmount: number;
    topCategoryPercentage: number;
  };
  categoryBreakdown: {
    category: ExpenseCategory;
    amount: number;
    percentage: number;
    transactionCount: number;
    averageAmount: number;
  }[];
  monthlyTrends: {
    month: string;
    amount: number;
    transactionCount: number;
    topCategory: ExpenseCategory;
  }[];
  paymentMethodBreakdown: {
    method: 'Online' | 'Cash';
    amount: number;
    percentage: number;
    transactionCount: number;
  }[];
  insights: string[];
  recommendations: string[];
}

interface ReportFilters {
  startDate: string;
  endDate: string;
  categories: ExpenseCategory[];
  groupBy: 'category' | 'month' | 'paymentMethod';
}

const ExpenseReportingDashboard: React.FC = () => {
  const [report, setReport] = useState<ExpenseReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<ReportFilters>({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth() - 2, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    categories: [],
    groupBy: 'category',
  });


  // Colors for charts
  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0', '#ffb347', '#87ceeb'];

  const generateReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Mock report data - replace with actual API call
      const mockReport: ExpenseReport = {
        reportPeriod: {
          startDate: new Date(filters.startDate),
          endDate: new Date(filters.endDate),
          daysIncluded: 90,
        },
        summary: {
          totalExpenses: 71700,
          totalTransactions: 19,
          averageExpenseAmount: 3773.68,
          topCategory: 'salary',
          topCategoryAmount: 45000,
          topCategoryPercentage: 62.7,
        },
        categoryBreakdown: [
          { category: 'salary', amount: 45000, percentage: 62.7, transactionCount: 3, averageAmount: 15000 },
          { category: 'electricity', amount: 15000, percentage: 20.9, transactionCount: 3, averageAmount: 5000 },
          { category: 'maintenance', amount: 8500, percentage: 11.9, transactionCount: 5, averageAmount: 1700 },
          { category: 'supplies', amount: 3200, percentage: 4.5, transactionCount: 8, averageAmount: 400 },
        ],
        monthlyTrends: [
          { month: '2026-01', amount: 24000, transactionCount: 8, topCategory: 'salary' },
          { month: '2026-02', amount: 24400, transactionCount: 7, topCategory: 'salary' },
          { month: '2026-03', amount: 23300, transactionCount: 4, topCategory: 'salary' },
        ],
        paymentMethodBreakdown: [
          { method: 'Cash', amount: 51700, percentage: 72.1, transactionCount: 14 },
          { method: 'Online', amount: 20000, percentage: 27.9, transactionCount: 5 },
        ],
        insights: [
          'Salary expenses account for 62.7% of total spending, indicating stable staffing costs',
          '72.1% of expenses are cash payments - consider digital payments for better tracking',
          'March expenses decreased by 4.5% compared to February',
          'Maintenance costs are relatively high at 11.9% - consider preventive measures',
        ],
        recommendations: [
          'Increase digital payments for better financial tracking and reduced cash handling',
          'Consider preventive maintenance to reduce repair costs',
          'Review salary-to-total-expense ratio for optimal budget allocation',
          'Implement bulk purchasing for supplies to reduce per-unit costs',
        ],
      };

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      setReport(mockReport);
    } catch (err) {
      setError('Failed to generate expense report');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    generateReport();
  }, [generateReport]);

  const handleExportReport = () => {
    if (!report) return;
    
    // Generate CSV export
    const csvData = [
      ['Expense Report'],
      ['Period', `${report.reportPeriod.startDate.toDateString()} - ${report.reportPeriod.endDate.toDateString()}`],
      [''],
      ['Summary'],
      ['Total Expenses', `₹${report.summary.totalExpenses.toLocaleString()}`],
      ['Total Transactions', report.summary.totalTransactions.toString()],
      ['Average Amount', `₹${report.summary.averageExpenseAmount.toFixed(2)}`],
      ['Top Category', `${report.summary.topCategory} (₹${report.summary.topCategoryAmount.toLocaleString()})`],
      [''],
      ['Category Breakdown'],
      ['Category', 'Amount', 'Percentage', 'Transactions'],
      ...report.categoryBreakdown.map(cat => [
        cat.category,
        `₹${cat.amount.toLocaleString()}`,
        `${cat.percentage}%`,
        cat.transactionCount.toString()
      ]),
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expense-report-${filters.startDate}-to-${filters.endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const SummaryCards = () => {
    if (!report) return null;

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Total Expenses
              </Typography>
              <Typography variant="h4">
                ₹{report.summary.totalExpenses.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {report.summary.totalTransactions} transactions
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="secondary" gutterBottom>
                Average Expense
              </Typography>
              <Typography variant="h4">
                ₹{report.summary.averageExpenseAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                per transaction
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main" gutterBottom>
                Top Category
              </Typography>
              <Typography variant="h4">
                {report.summary.topCategory}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {report.summary.topCategoryPercentage}% of total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="info.main" gutterBottom>
                Report Period
              </Typography>
              <Typography variant="h4">
                {report.reportPeriod.daysIncluded}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                days included
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const CategoryPieChart = () => {
    if (!report) return null;

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Expense Distribution by Category
          </Typography>
          <ResponsiveChartBox>
            <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={report.categoryBreakdown}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ category, percentage }) => `${category} (${percentage}%)`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="amount"
              >
                {report.categoryBreakdown.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`₹${(value as number).toLocaleString()}`, 'Amount']} />
            </PieChart>
          </ResponsiveContainer>
          </ResponsiveChartBox>
        </CardContent>
      </Card>
    );
  };

  const MonthlyTrendChart = () => {
    if (!report) return null;

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Monthly Expense Trends
          </Typography>
          <ResponsiveChartBox>
            <ResponsiveContainer width="100%" height={300}>
            <LineChart data={report.monthlyTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value, name) => [`₹${(value as number).toLocaleString()}`, name]} />
              <Legend />
              <Line type="monotone" dataKey="amount" stroke="#8884d8" strokeWidth={2} name="Amount" />
              <Bar dataKey="transactionCount" fill="#82ca9d" yAxisId="right" name="Transactions" />
            </LineChart>
          </ResponsiveContainer>
          </ResponsiveChartBox>
        </CardContent>
      </Card>
    );
  };

  const CategoryBarChart = () => {
    if (!report) return null;

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Expenses by Category
          </Typography>
          <ResponsiveChartBox>
            <ResponsiveContainer width="100%" height={300}>
            <BarChart data={report.categoryBreakdown}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="category" />
              <YAxis />
              <Tooltip formatter={(value) => [`₹${(value as number).toLocaleString()}`, 'Amount']} />
              <Bar dataKey="amount" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
          </ResponsiveChartBox>
        </CardContent>
      </Card>
    );
  };

  const InsightsAndRecommendations = () => {
    if (!report) return null;

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
                Key Insights
              </Typography>
              <Box>
                {report.insights.map((insight, index) => (
                  <Alert key={index} severity="info" sx={{ mb: 1 }}>
                    {insight}
                  </Alert>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
                Recommendations
              </Typography>
              <Box>
                {report.recommendations.map((recommendation, index) => (
                  <Alert key={index} severity="warning" sx={{ mb: 1 }}>
                    {recommendation}
                  </Alert>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const PaymentMethodAnalysis = () => {
    if (!report) return null;

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Payment Method Analysis
          </Typography>
          <Grid container spacing={2}>
            {report.paymentMethodBreakdown.map((method, index) => (
              <Grid item xs={12} sm={6} key={method.method}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: index === 0 ? 'success.light' : 'info.light' }}>
                  <Typography variant="h4" color={index === 0 ? 'success.dark' : 'info.dark'}>
                    {method.percentage}%
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {method.method} Payments
                  </Typography>
                  <Typography variant="body2">
                    ₹{method.amount.toLocaleString()} ({method.transactionCount} transactions)
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {/* Report Filters */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Report Configuration
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Group By</InputLabel>
              <Select
                value={filters.groupBy}
                label="Group By"
                onChange={(e) => setFilters(prev => ({ ...prev, groupBy: e.target.value as 'category' | 'month' | 'paymentMethod' }))}
              >
                <MenuItem value="category">Category</MenuItem>
                <MenuItem value="month">Month</MenuItem>
                <MenuItem value="paymentMethod">Payment Method</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<GetApp />}
              onClick={handleExportReport}
              disabled={!report}
            >
              Export Report
            </Button>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : report ? (
        <>
          <SummaryCards />
          
          <Grid container spacing={3}>
            {/* Charts Row 1 */}
            <Grid item xs={12} md={6}>
              <CategoryPieChart />
            </Grid>
            <Grid item xs={12} md={6}>
              <PaymentMethodAnalysis />
            </Grid>
            
            {/* Charts Row 2 */}
            <Grid item xs={12} md={6}>
              <MonthlyTrendChart />
            </Grid>
            <Grid item xs={12} md={6}>
              <CategoryBarChart />
            </Grid>
            
            {/* Insights Row */}
            <Grid item xs={12}>
              <InsightsAndRecommendations />
            </Grid>
          </Grid>
        </>
      ) : (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="h6" color="textSecondary">
            Configure report settings and click "Generate Report" to view analytics
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ExpenseReportingDashboard;